import { Controller } from "@hotwired/stimulus"

// Prepare SAP modal:
// - operator selector (default all)
// - period selector (YYYY-MM, available months from DB only)
// - visual hint: generated vs pending
export default class extends Controller {
  static targets = ["dialog", "operatorSelect", "periodSelect", "periodHint"]
  static values = {
    monthsByOperator: Object,
    generatedByOperator: Object,
    lockedUntilMonth: String
  }

  connect() {
    this.refreshPeriodOptions()
  }

  open(event) {
    if (event) event.preventDefault()
    if (!this.hasDialogTarget) return
    this.dialogTarget.classList.remove("hidden")
    setTimeout(() => this.dialogTarget.classList.add("opacity-100"), 10)
    this.refreshPeriodOptions()
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.hasDialogTarget) return
    this.dialogTarget.classList.remove("opacity-100")
    setTimeout(() => this.dialogTarget.classList.add("hidden"), 200)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  operatorChanged() {
    this.refreshPeriodOptions()
  }

  periodChanged() {
    this.updateHint()
  }

  refreshPeriodOptions() {
    if (!this.hasPeriodSelectTarget || !this.hasOperatorSelectTarget) return

    const operatorId = this.operatorSelectTarget.value || "0"
    const monthsMap = this.monthsByOperatorValue || {}
    const generatedMap = this.generatedByOperatorValue || {}
    const months = monthsMap[operatorId] || []

    this.periodSelectTarget.innerHTML = ""
    if (months.length === 0) {
      const emptyOpt = document.createElement("option")
      emptyOpt.value = ""
      emptyOpt.textContent = "No periods available"
      this.periodSelectTarget.appendChild(emptyOpt)
      this.periodSelectTarget.disabled = true
      this.updateHint()
      return
    }

    this.periodSelectTarget.disabled = false
    let firstEnabledMonth = null
    months.forEach((month) => {
      const generated = generatedMap?.[operatorId]?.[month] === true
      const locked = this.isMonthLocked(month)
      const option = document.createElement("option")
      option.value = month
      option.textContent = generated ? `${month} - generated` : `${month} - pending`
      if (locked) {
        option.textContent = `${option.textContent} - locked`
        option.disabled = true
      } else if (!firstEnabledMonth) {
        firstEnabledMonth = month
      }
      this.periodSelectTarget.appendChild(option)
    })

    if (firstEnabledMonth) {
      this.periodSelectTarget.value = firstEnabledMonth
    } else if (this.periodSelectTarget.options.length > 0) {
      this.periodSelectTarget.selectedIndex = 0
      this.periodSelectTarget.disabled = true
    }

    this.updateHint()
  }

  isMonthLocked(month) {
    const lockedUntilMonth = this.lockedUntilMonthValue
    return !!(lockedUntilMonth && month && month <= lockedUntilMonth)
  }

  updateHint() {
    if (!this.hasPeriodHintTarget || !this.hasPeriodSelectTarget || !this.hasOperatorSelectTarget) return

    const operatorId = this.operatorSelectTarget.value || "0"
    const month = this.periodSelectTarget.value
    const generatedMap = this.generatedByOperatorValue || {}
    const generated = generatedMap?.[operatorId]?.[month] === true
    const locked = this.isMonthLocked(month)

    if (!month) {
      this.periodHintTarget.textContent = ""
      this.periodHintTarget.className = "mt-2 text-xs"
      this.periodSelectTarget.classList.remove("text-gray-500", "text-green-700", "font-semibold")
      return
    }

    if (locked) {
      this.periodHintTarget.textContent = `Locked period. You can only prepare periods after ${this.lockedUntilMonthValue}.`
      this.periodHintTarget.className = "mt-2 text-xs text-red-600"
      this.periodSelectTarget.classList.add("text-gray-500")
      this.periodSelectTarget.classList.remove("text-green-700", "font-semibold")
      return
    }

    if (generated) {
      this.periodHintTarget.textContent = "This period already exists in sap_invoice_submissions (it will be recalculated only if status is prepared)."
      this.periodHintTarget.className = "mt-2 text-xs text-gray-500"
      this.periodSelectTarget.classList.add("text-gray-500")
      this.periodSelectTarget.classList.remove("text-green-700", "font-semibold")
    } else {
      this.periodHintTarget.textContent = "This period is pending generation."
      this.periodHintTarget.className = "mt-2 text-xs text-green-700 font-medium"
      this.periodSelectTarget.classList.remove("text-gray-500")
      this.periodSelectTarget.classList.add("text-green-700", "font-semibold")
    }
  }
}
